import React from "react";
import { Tooltip } from "@mui/material";
import PropTypes from "prop-types";

/**
 * CTooltip - A wrapper for MUI Tooltip with additional functionality
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The element that triggers the tooltip
 * @param {string} props.title - The content of the tooltip
 * @param {string} props.placement - Tooltip placement (top, bottom, left, right, etc.)
 * @param {boolean} props.arrow - Whether to show an arrow pointing to the trigger element
 * @param {Object} props.sx - Additional styling for the tooltip
 * @param {Object} props.theme - Theme object for styling
 * @param {Object} props.PopperProps - Additional props for the Popper component
 * @param {boolean} props.disableFocusListener - Disable the tooltip when the trigger is focused
 * @param {boolean} props.disableHoverListener - Disable the tooltip when the trigger is hovered
 * @param {boolean} props.disableTouchListener - Disable the tooltip when the trigger is touched
 * @param {number} props.enterDelay - Delay before showing the tooltip (ms)
 * @param {number} props.leaveDelay - Delay before hiding the tooltip (ms)
 * @param {function} props.onClose - Callback fired when the tooltip requests to be closed
 * @param {function} props.onOpen - Callback fired when the tooltip requests to be open
 * @param {boolean} props.open - If true, the tooltip is shown
 * @returns {JSX.Element}
 */
const CTooltip = ({
  children,
  title,
  placement = "top",
  arrow = true,
  sx = {},
  theme,
  PopperProps = {},
  disableFocusListener = false,
  disableHoverListener = false,
  disableTouchListener = false,
  enterDelay = 100,
  leaveDelay = 0,
  onClose,
  onOpen,
  open,
  ...rest
}) => {
  // Default styles based on theme (if provided)
  const defaultSx = theme
    ? {
        tooltip: {
          backgroundColor: theme.palette.grey[800],
          color: theme.palette.common.white,
          fontSize: theme.typography.pxToRem(12),
          padding: theme.spacing(1),
          borderRadius: theme.shape.borderRadius,
        },
        arrow: {
          color: theme.palette.grey[800],
        },
      }
    : {};

  // Merge default Popper props with provided ones
  const defaultPopperProps = {
    sx: { pointerEvents: "auto" },
    ...PopperProps,
  };

  return (
    <Tooltip
      title={title}
      placement={placement}
      arrow={arrow}
      sx={{ ...defaultSx, ...sx }}
      PopperProps={defaultPopperProps}
      disableFocusListener={disableFocusListener}
      disableHoverListener={disableHoverListener}
      disableTouchListener={disableTouchListener}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      onClose={onClose}
      onOpen={onOpen}
      open={open}
      {...rest}
    >
      {children}
    </Tooltip>
  );
};

CTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  placement: PropTypes.string,
  arrow: PropTypes.bool,
  sx: PropTypes.object,
  theme: PropTypes.object,
  PopperProps: PropTypes.object,
  disableFocusListener: PropTypes.bool,
  disableHoverListener: PropTypes.bool,
  disableTouchListener: PropTypes.bool,
  enterDelay: PropTypes.number,
  leaveDelay: PropTypes.number,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
};

export default CTooltip;